import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
	getDependencyCurrentFieldsWithoutOptions,
	getDependencyFieldsWithoutOptions,
} from '@utils/import/index';

import {
	ICommonDependencyFieldEntries,
	IFieldsSamples,
	ISystemFields,
	TCommonValue,
	TDependenciesImport,
	TSelectedValueFieldsMapping,
} from '@interfaces/import/import';

import { EDefaultValueType } from '@enums/common';

export const IMPORT_MAP_DATA = 'importMapData';
export const FIELD_NAME_FOR_DELETE = 'assetId';

interface IImportState {
	fieldsMapping: TSelectedValueFieldsMapping;
}

const initialState: IImportState = {
	fieldsMapping: {},
};

export const preparedRequiredFields = ({
	dependencies,
	state,
	defaultValue,
}: {
	dependencies: TDependenciesImport;
	state: IImportState;
	defaultValue: TCommonValue | null;
}) => {
	dependencies.forEach(dependency => {
		const isRequiredDependentFields = defaultValue !== null
			&& dependency.dependencyValues?.map(item => item.selectedValue).includes(defaultValue);

		//set is required to dependent field by custom value
		dependency.dependencyFields.forEach(dependentSystemFieldName => {
			if (Object.hasOwn(state.fieldsMapping, dependentSystemFieldName)) {
				const dependentSystemField = state.fieldsMapping[dependentSystemFieldName];

				state.fieldsMapping = {
					...state.fieldsMapping,
					[dependentSystemFieldName]: {
						...dependentSystemField,
						isRequired: isRequiredDependentFields,
					},
				};
			}
		});
	});
};

export const preparedFieldsWithOptions = ({
	dependencies,
	state,
	defaultValue,
}: {
	dependencies: TDependenciesImport;
	state: IImportState;
	defaultValue: TCommonValue | null;
}) => {
	dependencies.forEach((dependencyFieldWidthOptions) => {
		const firstSystemFieldName = dependencyFieldWidthOptions.dependencyFields[0];
		const dependentSystemField = state.fieldsMapping[firstSystemFieldName];
		const firstOptionsBySelectedValue = dependencyFieldWidthOptions.dependencyValues
			.find(item => item.selectedValue === defaultValue)?.options || [];
		// set options
		state.fieldsMapping = {
			...state.fieldsMapping,
			[firstSystemFieldName]: {
				...dependentSystemField,
				hasDefaultValue: !!firstOptionsBySelectedValue.length,
				isUseDefaultValue: !firstOptionsBySelectedValue.length
					? false :
					dependentSystemField.isUseDefaultValue,
				defaultValue: null,
				systemFieldsDefaultData: {
					defaultValueType: EDefaultValueType.array,
					...dependentSystemField.systemFieldsDefaultData,
					options: firstOptionsBySelectedValue.length
						? firstOptionsBySelectedValue
						: undefined,
				},
			},
		};
	});
};

const importMapData = createSlice({
	name: IMPORT_MAP_DATA,
	initialState,
	reducers: {
		setDefaultFields: (state, action: PayloadAction<{
			systemFields: Array<ISystemFields>;
			fieldsSamples: Array<IFieldsSamples>;
			isClear?: boolean;
			isDeleteRule: boolean;
		}>) => {
			const { systemFields, fieldsSamples, isClear = false, isDeleteRule } = action.payload;

			const commonDependencyFieldEntries = systemFields
				.reduce<Array<ICommonDependencyFieldEntries>>((acc, field) => {
					const dependencyFieldNames = (!field.hasDefaultValue && field.hasDependency && field?.dependencies)
						? field.dependencies.reduce<Array<string>>((acc, dependency) => {
							acc = [...acc, ...dependency.dependencyFields];

							return acc;
						}, []) : [];

					const withSample = !!fieldsSamples
						.find(sample => sample?.autocompleteField?.name === dependencyFieldNames[0])?.sample;

					return dependencyFieldNames.length
						? [...acc, {
							systemFieldName: field.systemFieldName,
							dependencyFieldName: dependencyFieldNames[0],
							withSample,
						}]
						: acc;
				}, []);

			const systemFieldsFilled = systemFields.reduce((acc, field) => {
				const currentFieldsSample = fieldsSamples
					.find(sample => sample?.autocompleteField?.name === field.systemFieldName);
				const fieldNameAutocomplete = currentFieldsSample?.name || null;
				const sample = currentFieldsSample?.sample || '';
				const customRequiredField = commonDependencyFieldEntries
					.find(entry => entry.systemFieldName === field.systemFieldName);

				if ((isDeleteRule && field.systemFieldName === FIELD_NAME_FOR_DELETE) || !isDeleteRule) {
					acc = {
						...acc,
						[field.systemFieldName]: {
							...field,
							fieldName: isClear ? null : fieldNameAutocomplete,
							isUseDefaultValue: false,
							defaultValue: field.hasDefaultValue && !isClear
								? field?.systemFieldsDefaultData?.defaultValue
								: null,
							sample: isClear ? '' : sample,
							isRequired: customRequiredField ? !customRequiredField.withSample : field.isRequired,
						},
					};
				}

				return acc;
			}, {});

			state.fieldsMapping = systemFieldsFilled;
		},
		changeFieldName: (state, action: PayloadAction<{
			fieldName: string;
			value: string | null;
			fieldsSamples: Array<IFieldsSamples>;
		}>) => {
			const { fieldName, value, fieldsSamples } = action.payload;

			const currentFieldsSample = fieldsSamples
				.find(sample => sample?.name === value);
			const sample = value === null ? '' : currentFieldsSample?.sample || '';

			if (Object.hasOwn(state.fieldsMapping, fieldName)) {
				const currentField = state.fieldsMapping[fieldName];

				if (!currentField.hasDefaultValue && currentField.hasDependency) {
					currentField.dependencies?.forEach(dependency => {
						const isRequiredDependentFields = !value?.length;

						//set is required to dependent field by field from document
						dependency.dependencyFields.forEach(dependentSystemFieldName => {
							if (Object.hasOwn(state.fieldsMapping, dependentSystemFieldName)) {
								const dependentSystemField = state.fieldsMapping[dependentSystemFieldName];

								state.fieldsMapping = {
									...state.fieldsMapping,
									[dependentSystemFieldName]: {
										...dependentSystemField,
										isRequired: isRequiredDependentFields,
									},
								};
							}
						});
					});
				}

				state.fieldsMapping = {
					...state.fieldsMapping,
					[fieldName]: {
						...currentField,
						fieldName: value,
						sample,
					},
				};
			}
		},
		changeDefaultValue: (state, action: PayloadAction<{
			fieldName: string;
			defaultValue: string | number | boolean | null;
		}>) => {
			const { fieldName, defaultValue } = action.payload;

			if (Object.hasOwn(state.fieldsMapping, fieldName)) {
				const currentField = state.fieldsMapping[fieldName];

				if (currentField.hasDefaultValue && currentField.hasDependency) {
					//set options to dependent custom value
					const shouldResetOptionsForDependentDefaultValue = !!currentField?.dependencies
						?.reduce<Array<boolean>>((acc, dependency) => {
							const hasOptions = dependency.dependencyValues
								.some(dependencyValue => dependencyValue?.options?.length);

							return ([...acc, hasOptions]);
						}, []).includes(true);

					if (shouldResetOptionsForDependentDefaultValue) {
						// ONLY WITH OPTIONS
						const dependencyFieldsWidthOptions = currentField?.dependencies
							?.reduce<TDependenciesImport>((acc, dependency) => {
								const dependencyValuesWidthOptionsBySelectValue = dependency.dependencyValues
									.filter(dependencyValue => dependencyValue?.options?.length);

								if (dependencyValuesWidthOptionsBySelectValue.length) {
									return ([...acc, {
										...dependency,
										dependencyValues: dependencyValuesWidthOptionsBySelectValue,
									}]);
								}
								return acc;
							}, []);

						const dependencyCurrentFieldsWidthOptions = dependencyFieldsWidthOptions
							?.filter(dependency => defaultValue && dependency.dependencyValues
								?.map(item => item.selectedValue).includes(defaultValue));

						// by selected/current field
						if (dependencyCurrentFieldsWidthOptions?.length) {
							preparedFieldsWithOptions({
								dependencies: dependencyCurrentFieldsWidthOptions,
								state,
								defaultValue,
							});
							// reset by all dependent fields with options
						} else if (dependencyFieldsWidthOptions?.length) {
							preparedFieldsWithOptions({
								dependencies: dependencyFieldsWidthOptions,
								state,
								defaultValue,
							});
						}
					}

					// ONLY REQUIRED
					const dependencyCurrentFieldsWithoutOptions = getDependencyCurrentFieldsWithoutOptions({
						currentField,
						defaultValue,
					});

					const dependencyFieldsWithoutOptions = getDependencyFieldsWithoutOptions({
						currentField,
						defaultValue,
					});

					if (dependencyFieldsWithoutOptions?.length) {
						// reset by all dependent fields without options
						preparedRequiredFields({
							dependencies: dependencyFieldsWithoutOptions,
							state,
							defaultValue,
						});
					}
					// by selected/current field
					if (dependencyCurrentFieldsWithoutOptions?.length) {
						// set required fields
						preparedRequiredFields({
							dependencies: dependencyCurrentFieldsWithoutOptions,
							state,
							defaultValue,
						});
					}
				}

				//set selected custom value
				state.fieldsMapping = {
					...state.fieldsMapping,
					[fieldName]: {
						...currentField,
						defaultValue: defaultValue,
					},
				};
			}
		},
		toggleIsUseDefaultValue: (state, action: PayloadAction<{ fieldName: string }>) => {
			const { fieldName } = action.payload;

			if (Object.hasOwn(state.fieldsMapping, fieldName)) {
				state.fieldsMapping = {
					...state.fieldsMapping,
					[fieldName]: {
						...state.fieldsMapping[fieldName],
						isUseDefaultValue: !state.fieldsMapping[fieldName].isUseDefaultValue,
					},
				};
			}
		},
		clearFieldsMapping: (state) => {
			state.fieldsMapping = initialState.fieldsMapping;
		},
	},
});

export const {
	setDefaultFields,
	changeFieldName,
	toggleIsUseDefaultValue,
	changeDefaultValue,
	clearFieldsMapping,
} = importMapData.actions;

export default importMapData.reducer;
